/**
 * Finish Account Step - Subscription Purchase
 *
 * Final step in subscription purchase flow - account creation and subscription purchase.
 *
 * Layout:
 * - Left Column: Subscription Card → Billing Details → Price Summary (stacked)
 * - Right Column: Complete Account Form + Action Buttons (full height)
 * - Mobile: Right column first (form on top), then left column cards
 *
 * States:
 * - Account state is 'incomplete' or 'pending': Show "Finish your account" with account completion form
 * - Otherwise: Show "Review subscription" without account form (existing users)
 *
 * @module pages/subscription/steps
 */

import { needsAccountSetup } from '@butter/library/shared';
import { useState, useMemo } from "react";
import {
  useSubscriptionPurchase,
  useSubscriptionStep,
  useCreateUserMembership,
  useCreateUserPass,
} from "~/services/internalApi/subscription";
import { getPromotionRejection } from "~/lib/apiClient";
import type { ApiErrorResult } from "~/lib/apiClient/types";
import { useClubQuery } from "~/services/internalApi/club/hooks/useClubQuery";
import { useAuth } from "~/services/auth";
import type { User } from "~/services/auth/types";
import { CompleteAccountForm } from "../../booking/components/CompleteAccountForm";
import { BookingDetailsCard } from "@butter/ui/domains/booking";
import { PriceSummaryCard } from "@butter/ui/domains/billing";
import { SubscriptionPlanCard } from "@butter/ui/domains/subscriptions";
import { calcBillingDetails } from "@butter/ui/utils";
import { TwoColumnLayout } from "~/ui/layout/TwoColumnLayout";
import { useTenantNavigate } from "~/lib/navigation";
import { ArrowIcon } from "@butter/ui/icons";
import { addBreadcrumb } from "@butter/monitoring";
import { reportPaymentClientError } from "~/features/payment";
import { useSubscriptionPurchasePricing } from "./useSubscriptionPurchasePricing";
import { QuoteFailedBanner, QuoteRefusedPanel, PromoNotice } from "./QuoteBanners";
import { PromoCodeField } from "./PromoCodeField";
import { giftOrderFields, passReceiptCompositeId, refusedRecipientIndex, refusedRecipientLabel } from "./giftRecipients";
import { useWaiverGateHold } from "../../../features/waivers/waiverGateHold";

/**
 * Finish Account Step Component
 *
 * Layout varies based on the account state:
 * - Account state is 'incomplete' or 'pending': Shows account completion form
 * - Otherwise: Shows review only (no form)
 */
export function FinishAccountStep() {
  // Account completion flips auth mid-step; hold the waiver entry gate so it
  // can't swap the purchase flow for a SignWaiversScreen before the purchase
  // lands (see features/waivers/waiverGateHold.ts). The subscription flow
  // captures no waiver by design — the member meets the gate on their next
  // gated flow, and the receipt route renders regardless.
  useWaiverGateHold();

  const navigate = useTenantNavigate();
  const { user: authenticatedUser } = useAuth();
  const { data: club } = useClubQuery();

  const {
    selectedSubscription,
    guestUser,
    paymentMethodId,
    paymentProvider,
    clearPurchase,
    promoCode,
    clearPromoCode,
    // The order was composed on the Review step; this step only sends it.
    quantity,
    // Tri-state in the context; `null` means unanswered. Only a GUEST reaches
    // this step (auto-advance is gated on `!isAuthenticated`), and a guest holds
    // no plan, so there is no default to apply here — unanswered is `false`.
    // The Review step resolves the locked/defaulted value for everyone else.
    giftAll,
    recipients,
    setGuestUser,
  } = useSubscriptionPurchase();

  const { goToPreviousStep } = useSubscriptionStep();

  const [error, setError] = useState<string | null>(null);
  // Whether the current `error` is the promo auto-drop — the banner's
  // `data-kind` e2e hook (specs assert classification, never copy).
  const [promoDropped, setPromoDropped] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Determine current user - prefer guestUser from context (set by SubscriptionReviewStep), fallback to authenticated user
  const currentUser = guestUser || authenticatedUser;

  /**
   * Gate for rendering CompleteAccountForm. `pending` is bundled with `incomplete`
   * because neither ever set a user-chosen password — see PaymentDetailsStep.tsx for
   * the full rationale. The form posts NO setupToken, so the `pending` half works only
   * while the TEMPORARY G23 window is open in the API.
   * @see /apps/api/src/services/auth/docs/KNOWN_GAPS.md G23, G8
   */
  const isIncompleteUser = currentUser ? needsAccountSetup(currentUser) : false;

  // Server-authored price (D12) — same hook + key as SubscriptionReviewStep
  // (React Query dedupes), so this step renders the number the buyer already
  // saw there and the number the charge will invoice.
  const {
    query: quoteQuery,
    refused: quoteRefused,
    quoteUnavailable,
    priceUnknown,
    subtotal,
    tax,
    total,
    taxRate,
    processingFee,
    processingFeeRate,
    setupFee,
    lineItems: displayLineItems,
    discountTotal,
    promotion,
  } = useSubscriptionPurchasePricing(
    selectedSubscription,
    club?.id,
    promoCode,
    quantity,
    // `authenticatedUser`, not `guestUser`: the guest funnel's
    // `/users/incomplete` creates and ENROLS the account but establishes no
    // session, so a guest arriving here still quotes loosely. The identity
    // turns up mid-step, when CompleteAccountForm's register() lands — which
    // is why this is gated on the session existing rather than on the step: a
    // re-quote that fired before the token landed would 401.
    !!authenticatedUser,
  );

  // Only a code the quote actually priced rides the purchase — never one the
  // quote already refused (the server would refuse it again, less gracefully).
  const promoCodeForPurchase = promotion?.applied ? promotion.code : undefined;

  // Calculate billing details
  const billingDetails = useMemo(() => {
    if (!selectedSubscription || !club?.timezone) {
      return { startDate: "", billingCycle: "", nextBillingDate: "" };
    }
    return calcBillingDetails(selectedSubscription, club.timezone);
  }, [selectedSubscription, club]);

  /**
   * The one failure path for both create mutations (envelope shape): the
   * expected business failure arrives on the ENVELOPE — including
   * `promotion_rejected`, whose answer is the auto-drop (clear the code, the
   * key change requotes at list price, show the server's copy).
   */
  const handleCreateFailure = (result: ApiErrorResult, fallback: string) => {
    console.error("[FinishAccountStep] Create failed:", result.message);
    const rejection = getPromotionRejection(result);
    if (result.error?.code === 'gift_recipient_ineligible') {
      // Refused BEFORE any charge. The recipient block lives on the Review step, so
      // say WHO and send the buyer back — keep the code, the quantity, the rest.
      const details = result.error.details as { index?: unknown } | null | undefined;
      const who = refusedRecipientLabel(
        recipients,
        typeof details?.index === 'number' ? details.index : refusedRecipientIndex(result.error.field),
      );
      setPromoDropped(false);
      setError(
        `${who ? `${who}: ` : ''}${result.message || fallback} Go back to change who you're gifting to.`,
      );
    } else if (rejection?.quantityFixable) {
      // The code is fine; the order is the wrong size for it — keep
      // the code. The quantity is changed back on the Review step.
      setPromoDropped(false);
      setError(`${rejection.message} Go back to change how many passes you're buying.`);
    } else if (rejection) {
      clearPromoCode();
      setPromoDropped(true);
      setError(
        `${rejection.message} The code was removed — you can complete your purchase at the regular price.`,
      );
    } else {
      setPromoDropped(false);
      setError(result.message || fallback);
    }
    setIsProcessing(false);
  };

  // Mutation hooks for creating memberships and passes
  const createMembership = useCreateUserMembership({
    onSuccess: (data) => {
      console.log(
        "[FinishAccountStep] Membership created:",
        data.userMembership.id
      );

      addBreadcrumb(
        "subscription",
        "Membership purchase completed successfully",
        {
          userMembershipId: data.userMembership.id,
          membershipId: data.userMembership.membershipId,
          userId: data.userMembership.userId,
        },
        "info"
      );

      // Clear payment data from context
      clearPurchase();

      // Navigate to confirmation page with data in state for immediate rendering
      navigate(`/subscriptions/confirmed/membership-${data.userMembership.id}`, {
        state: {
          subscription: data,
          type: 'membership',
          pricing: {
            subtotal,
            tax,
            total,
            taxRate,
            setupFee,
            // Receipt Discount row (7a+m) — present only when a promo priced in.
            ...(discountTotal > 0 && { discountTotal, promoLabel: promotion?.label }),
          },
        }
      });
      // AFTER navigate: clearing changes the quote key and would fire a
      // throwaway list-price refetch on a page that is about to unmount.
      clearPromoCode();
    },
    onFailure: (result) => handleCreateFailure(result, "Failed to create membership"),
    onError: (error) => {
      console.error("[FinishAccountStep] Membership create threw:", error);
      setError(error.message || "Failed to create membership");
      setIsProcessing(false);
    },
  });

  const createPass = useCreateUserPass({
    onSuccess: (data) => {
      // A PURE GIFT returns no `userPass` — the buyer kept none,
      // so the receipt is keyed by the invoice they paid instead.
      const receipt = passReceiptCompositeId(data);
      if (!receipt) {
        // The server returned neither a pass nor an invoice. Unreachable through
        // the public API — a pure gift needs a paid pass, and a kept-one order
        // always has the buyer's row — but the money has already moved, so this
        // must not fail silently. Deliberately NOT redirected: `/subscriptions`
        // is the purchase funnel, and dropping a charged buyer back at step 0
        // is worse than the invalid-link panel they get below.
        reportPaymentClientError('finishaccountstep_receipt', new Error('Purchase returned neither a userPass nor an invoice'), {
          kind: 'response_anomaly',
          surface: 'web_subscription',
          extra: { giftedCount: data.giftedPasses?.length ?? 0 },
        });
      }
      console.log("[FinishAccountStep] Pass created:", receipt);

      addBreadcrumb(
        "subscription",
        "Pass purchase completed successfully",
        {
          userPassId: data.userPass?.id ?? null,
          passId: data.userPass?.passId ?? data.pass?.id ?? null,
          userId: data.userPass?.userId ?? null,
          giftedCount: data.giftedPasses?.length ?? 0,
        },
        "info"
      );

      // Clear payment data from context
      clearPurchase();

      // Navigate to confirmation page with data in state for immediate rendering
      navigate(`/subscriptions/confirmed/${receipt}`, {
        state: {
          subscription: data,
          type: data.userPass ? 'pass' : 'gift',
          pricing: {
            subtotal,
            tax,
            total,
            taxRate,
            setupFee, // Always 0 for passes, but include for consistency
            // Receipt Discount row (7a) — present only when a promo priced in.
            ...(discountTotal > 0 && { discountTotal, promoLabel: promotion?.label }),
          },
        }
      });
      // The code is spent — a stale capture must not ride into the member's
      // NEXT purchase. AFTER navigate: clearing changes the quote key and
      // would fire a throwaway list-price refetch pre-unmount.
      clearPromoCode();
    },
    onFailure: (result) => handleCreateFailure(result, "Failed to create pass"),
    onError: (error) => {
      console.error("[FinishAccountStep] Pass create threw:", error);
      setError(error.message || "Failed to create pass");
      setIsProcessing(false);
    },
  });

  // Handle account completion for NEW users (account state 'incomplete' or 'pending')
  const handleAccountComplete = async (completedUser: User) => {
    // The account now exists and is signed in. Adopt it in place of the incomplete guest,
    // so a retry on this step — after a refused purchase — confirms as an existing user
    // instead of submitting the account form again ("User not found").
    setGuestUser(completedUser, true);
    try {
      setIsProcessing(true);
      setError(null);
      setPromoDropped(false);

      // Validate all required data
      if (!currentUser) {
        throw new Error(
          "User not found. Please restart the subscription purchase process."
        );
      }
      if (!selectedSubscription) {
        throw new Error("No subscription selected. Please start over.");
      }
      if (!paymentMethodId || !paymentProvider) {
        throw new Error(
          "Payment method not found. Please go back and re-enter payment."
        );
      }
      if (!club) {
        throw new Error(
          "Club information not loaded. Please refresh the page."
        );
      }
      if (priceUnknown || !quoteQuery.data) {
        throw new Error("The price has not been confirmed yet. Please wait a moment.");
      }

      console.log(
        "[FinishAccountStep] Account completed, creating subscription..."
      );

      // Note: Account setup already happened in CompleteAccountForm
      // Now we just need to create the subscription

      // Convert userId to number (backend API expects number)
      const userId =
        typeof currentUser.id === "string"
          ? parseInt(currentUser.id, 10)
          : currentUser.id;

      if (isNaN(userId)) {
        throw new Error("Invalid user ID. Please try logging in again.");
      }

      // Ensure subscription ID is a number
      const subscriptionId = typeof selectedSubscription.id === 'number' 
        ? selectedSubscription.id 
        : parseInt(String(selectedSubscription.id), 10);

      if (isNaN(subscriptionId)) {
        throw new Error("Invalid subscription ID");
      }

      if (!club?.id) {
        throw new Error("Club information not available");
      }

      // Create subscription based on type - now passing paymentMethodId
      if (selectedSubscription.subscriptionType === "membership") {
        await createMembership.createUserMembership({
          userId,
          membershipId: subscriptionId,
          paymentMethodId, // Pass the payment method ID from context
          ...(promoCodeForPurchase && { promoCode: promoCodeForPurchase }),
        });
      } else {
        await createPass.createUserPass({
          userId,
          passId: subscriptionId,
          clubId: club.id,
          paymentMethodId, // Pass the payment method ID from context
          ...(promoCodeForPurchase && { promoCode: promoCodeForPurchase }),
          // `?? false`, not the Review step's resolved value: only a GUEST
          // reaches this step (auto-advance is gated on `!isAuthenticated`), and
          // a guest holds no plan, so there is no lock or default to apply.
          ...giftOrderFields(recipients, quantity, giftAll ?? false),
        });
      }
    } catch (err) {
      console.error(
        "[FinishAccountStep] Failed to create subscription:",
        err
      );
      // Only the pre-mutate guard throws land here now — an API failure
      // resolves as an envelope and is owned by handleCreateFailure.
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create subscription. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // Handle subscription creation for existing users (neither incomplete nor pending)
  const handleExistingUserSubscription = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setPromoDropped(false);

      // Validate all required data
      if (!currentUser) {
        throw new Error(
          "User not found. Please restart the subscription purchase process."
        );
      }
      if (!selectedSubscription) {
        throw new Error("No subscription selected. Please start over.");
      }
      if (!paymentMethodId || !paymentProvider) {
        throw new Error(
          "Payment method not found. Please go back and re-enter payment."
        );
      }
      if (!club) {
        throw new Error(
          "Club information not loaded. Please refresh the page."
        );
      }
      if (priceUnknown || !quoteQuery.data) {
        throw new Error("The price has not been confirmed yet. Please wait a moment.");
      }

      console.log(
        "[FinishAccountStep] Creating subscription for existing user..."
      );

      // Convert userId to number
      const userId =
        typeof currentUser.id === "string"
          ? parseInt(currentUser.id, 10)
          : currentUser.id;

      if (isNaN(userId)) {
        throw new Error("Invalid user ID. Please try logging in again.");
      }

      // Ensure subscription ID is a number
      const subscriptionId = typeof selectedSubscription.id === 'number' 
        ? selectedSubscription.id 
        : parseInt(String(selectedSubscription.id), 10);

      if (isNaN(subscriptionId)) {
        throw new Error("Invalid subscription ID");
      }

      if (!club?.id) {
        throw new Error("Club information not available");
      }

      // Create subscription based on type - now passing paymentMethodId
      if (selectedSubscription.subscriptionType === "membership") {
        await createMembership.createUserMembership({
          userId,
          membershipId: subscriptionId,
          paymentMethodId, // Pass the payment method ID from context
          ...(promoCodeForPurchase && { promoCode: promoCodeForPurchase }),
        });
      } else {
        await createPass.createUserPass({
          userId,
          passId: subscriptionId,
          clubId: club.id,
          paymentMethodId, // Pass the payment method ID from context
          ...(promoCodeForPurchase && { promoCode: promoCodeForPurchase }),
          // `?? false`, not the Review step's resolved value: only a GUEST
          // reaches this step (auto-advance is gated on `!isAuthenticated`), and
          // a guest holds no plan, so there is no lock or default to apply.
          ...giftOrderFields(recipients, quantity, giftAll ?? false),
        });
      }
    } catch (err) {
      console.error(
        "[FinishAccountStep] Failed to create subscription:",
        err
      );
      // Only the pre-mutate guard throws land here now — an API failure
      // resolves as an envelope and is owned by handleCreateFailure.
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create subscription. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // Combined handler for "Save and Confirm" button
  const handleSaveAndConfirm = async () => {
    if (isIncompleteUser) {
      // Form submission will trigger handleAccountComplete
      return;
    } else {
      // Existing user - directly create subscription
      await handleExistingUserSubscription();
    }
  };

  // Redirect if no subscription selected
  if (!selectedSubscription) {
    return null;
  }

  return (
    <div className="">
      {/* Back to Review Link */}
      <button
        onClick={goToPreviousStep}
        disabled={isProcessing}
        data-testid="subscription-finish-back-btn"
        className="inline-flex items-center gap-2 text-sm text-fg-quaternary text-interactive:text-fg-tertiary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowIcon className="h-4 w-4 rotate-180" />
        Back to Review
      </button>

      {/* Error Message */}
      {error && (
        <div
          className="rounded-lg border-2 border-red-200 bg-red-50 p-4 mt-6"
          data-testid="subscription-finish-error"
          data-kind={promoDropped ? 'promotion_rejected' : 'error'}
        >
          <p className="text-sm text-red-800">⚠️ {error}</p>
        </div>
      )}

      {/* Two Column Layout: Left = Cards Stack, Right = Form + Actions */}
      <TwoColumnLayout
        gap="xl"
        mobileOrder="right-first"
        left={
          <>
            {/* Subscription Card */}
            <SubscriptionPlanCard
              plan={{
                id: selectedSubscription.id,
                name: selectedSubscription.name,
                description: selectedSubscription.description,
                price: selectedSubscription.price,
                billingFrequency: selectedSubscription.billingFrequency,
                validityDays: selectedSubscription.validityDays,
                color: selectedSubscription.color,
                subscriptionType: selectedSubscription.subscriptionType,
              }}
            />

            {/* Billing Details */}
            <BookingDetailsCard
              title="Billing Details"
              startDate={billingDetails.startDate}
              billingCycle={billingDetails.billingCycle}
              nextBillingDate={billingDetails.nextBillingDate}
              subscriptionType={selectedSubscription.subscriptionType}
            />

            <PromoCodeField promotion={promotion} />

            {promotion && !promotion.applied && (
              <PromoNotice
                variant="refused"
                reason={promotion.reason}
                testId="subscription-finish-promo-refused"
              >
                {promotion.message ?? 'This promo code could not be applied.'} You can still
                complete your purchase at the regular price.
              </PromoNotice>
            )}

            {quoteUnavailable && (
              <QuoteFailedBanner
                onRetry={() => void quoteQuery.refetch()}
                testId="subscription-finish-quote-failed"
              />
            )}

            {/* Price Summary — or the server's refusal, which takes priority. */}
            {quoteRefused && (quoteQuery.data?.errors.length ?? 0) > 0 ? (
              <QuoteRefusedPanel
                errors={quoteQuery.data?.errors ?? []}
                testId="subscription-finish-quote-refused"
              />
            ) : (
              <PriceSummaryCard
                pending={priceUnknown}
                lineItems={displayLineItems}
                showSubtotal
                subtotal={subtotal}
                tax={tax}
                total={total}
                taxRatePercent={taxRate}
                processingFee={processingFee}
                processingFeeRate={processingFeeRate}
                subscriptionDiscount={discountTotal > 0 ? discountTotal : undefined}
                discountLabel={promotion?.applied ? promotion.label : undefined}
              />
            )}
          </>
        }
        right={
          <>
            {/* Complete Account Form - Only shown if the account state is 'incomplete' or 'pending' */}
            {isIncompleteUser && currentUser && (
              <CompleteAccountForm
                userId={currentUser.id}
                phoneRegion={club?.country}
                email={currentUser.email}
                onComplete={handleAccountComplete}
                clubId={club?.id}
              />
            )}

            {/* Action Buttons */}
            <div className="flex flex-col text-sm sm:flex-row gap-3">
              {/* Back Button */}
              <button
                onClick={goToPreviousStep}
                disabled={isProcessing}
                className="flex-1 px-6 py-2 bg-white text-gray-700 border border-theme-600 rounded-full font-medium hover:bg-gray-50 focus:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Back
              </button>

              {/* Pay and Confirm Button */}
              {isIncompleteUser ? (
                <button
                  type="submit"
                  form="complete-account-form"
                  disabled={isProcessing || !club?.id || priceUnknown}
                  data-testid="subscription-finish-pay-btn"
                  className="flex-1 px-6 py-2 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing
                    ? "Processing..."
                    : priceUnknown
                      ? "Waiting for price…"
                      : `Pay $${total.toFixed(2)}`}
                </button>
              ) : (
                <button
                  onClick={handleSaveAndConfirm}
                  disabled={isProcessing || !club?.id || priceUnknown}
                  data-testid="subscription-finish-pay-btn"
                  className="flex-1 px-6 py-2 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing
                    ? "Processing..."
                    : priceUnknown
                      ? "Waiting for price…"
                      : `Pay $${total.toFixed(2)}`}
                </button>
              )}
            </div>
          </>
        }
      />

      {/* No User Data: Error State */}
      {!currentUser && (
        <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-8 mt-6">
          <div className="text-center">
            <p className="mb-4 text-yellow-800">
              ⚠️ Missing user information. Please restart the subscription
              purchase process.
            </p>
            <button
              onClick={goToPreviousStep}
              className="px-6 py-2 bg-theme-600 text-white rounded-lg font-medium hover:bg-theme-700"
            >
              Go Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
